/**
 * Blocking cookie-consent wall (RQ10409305 "blocking cookie banner",
 * RQ10773473 "1P cookies only after banner acknowledgment", RQ78378/RQ78379).
 *
 * A focus-trapping, non-dismissible full-screen modal shown on first interactive
 * load until the user acknowledges. The app behind it is NOT mounted, so no
 * first-party cookie or cookie-like storage is written before acknowledgment.
 *
 * Exactly two primary actions, both of which grant the mandatory strictly-
 * necessary cookies (auth, load-balancing affinity, the consent record itself):
 *   - "Essential cookies only"  → non-essential (analytics) rejected
 *   - "Accept all cookies"      → analytics also allowed (GPC still overrides)
 * A secondary "Manage preferences" link opens the granular surface; saving there
 * also acknowledges.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createDebug } from '../../utils/debugLog'
import { useConsent } from '../../consent/useConsent'
import { APP_NAME } from '../../constants/brand'

const log = createDebug('dw:cookie-consent:blocking-modal')

export default function ConsentBlockingModal() {
  const { t } = useTranslation('settings')
  const { acceptAll, rejectNonEssential, openSettings, gpc, isSettingsOpen } = useConsent()
  const dialogRef = useRef<HTMLDivElement>(null)
  const acceptRef = useRef<HTMLButtonElement>(null)

  // While the granular preferences modal is open (from "Manage preferences"),
  // hide this wall so the two don't fight over z-index / focus trap. The app is
  // still not mounted behind it, so the user remains blocked until they choose.
  // Hooks must run unconditionally, so this guard sits before the effect's body.
  const hidden = isSettingsOpen

  // Focus the primary action on mount and trap focus inside the dialog — the
  // page behind is inert (not mounted), but we still keep keyboard focus here.
  useEffect(() => {
    if (hidden) return
    log('shown')
    acceptRef.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [hidden])

  if (hidden) return null

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-md p-4"
      role="presentation"
      data-testid="consent-blocking-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-blocking-title"
        aria-describedby="consent-blocking-desc"
        data-testid="consent-blocking-modal"
        className="w-full max-w-lg rounded-lg border border-border bg-surface-1 p-6 shadow-2xl"
      >
        <h2
          id="consent-blocking-title"
          className="text-base font-semibold text-foreground"
        >
          {t('consentBlocking.cookiesOn', { appName: APP_NAME })}
        </h2>
        <p
          id="consent-blocking-desc"
          className="mt-2 text-sm text-muted-foreground"
        >
          {t('consentBlocking.description')}
        </p>

        {gpc && (
          <div
            className="mt-3 rounded-md bg-surface-2 px-3 py-2 text-xs font-medium text-muted-foreground"
            data-testid="consent-blocking-gpc-note"
          >
            {t('consentBlocking.gpcNote')}
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
          <button
            ref={acceptRef}
            type="button"
            onClick={() => {
              log('click accept-all')
              acceptAll()
            }}
            data-testid="consent-accept-all"
            className="flex-1 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 cursor-pointer"
          >
            {t('consentBlocking.acceptAll')}
          </button>
          {/* Equal-prominence reject (GDPR Art. 7): same size/weight as Accept. */}
          <button
            type="button"
            onClick={() => {
              log('click essential-only')
              rejectNonEssential()
            }}
            data-testid="consent-essential-only"
            className="flex-1 rounded-md bg-surface-2 px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-surface-3 cursor-pointer"
          >
            {t('consentBlocking.essentialOnly')}
          </button>
        </div>

        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => {
              log('click manage-preferences')
              openSettings()
            }}
            data-testid="consent-blocking-manage"
            className="text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground cursor-pointer"
          >
            {t('consentBlocking.managePreferences')}
          </button>
        </div>
      </div>
    </div>
  )
}
